import type { NotificationPatchInput } from './raise-input.js';

/**
 * Questions whose answer is a record in the master data, not a value someone thinks up.
 *
 * "Welche Firma ist das?" and "Welche Materialnummer?" are lookups, and the platform can do a
 * lookup better than a person writing prose about one. Asking for it in free text put the user
 * in the position of describing a record the system was then going to search for anyway — and
 * every ambiguity in that description came back as a rejection. Picking the record removes the
 * describing step altogether: what travels is the key, and a key resolves to exactly one row.
 *
 * Shared between the question card, which decides whether to offer a picker, and the answer
 * path, which turns the picked key into a patch. One module so the two cannot drift: a picker
 * the server does not accept is worse than no picker.
 */

/** The virtual field a reading uses to name the customer, by name or by number. */
export const COMPANY_REFERENCE_FIELD = 'company';

export type ReferenceKind = 'company' | 'product';

/** The parts of a question that decide what it is asking for. */
export interface ReferenceQuestion {
  fieldKey?: string | null;
  productId?: string | null;
}

/** Header fields that all describe the same thing: which customer this request belongs to. */
const COMPANY_FIELDS = ['requestingCompany', 'debitor', 'name'];

/**
 * Which master record settles this question — or `null` where nothing does.
 *
 * Keyed off `fieldKey`, which is set only when a question has exactly one missing field. A
 * question standing for two gaps at once ("Position 20: 2 Pflichtangaben fehlen") is left with
 * free text: a picker would settle one half and quietly imply it had settled both.
 */
export function referenceKindOf(question: ReferenceQuestion): ReferenceKind | null {
  const field = question.fieldKey;
  if (!field) return null;
  if (COMPANY_FIELDS.includes(field)) return 'company';
  if (field === 'productNumber' && question.productId) return 'product';
  return null;
}

/**
 * The patch a picked record becomes.
 *
 * A company is written as a company *reference* rather than straight into `debitor`, so it
 * takes the same route as a name typed into a mail: the master record is read once more and
 * the offer gets the debitor together with the spelling the master data uses, not two fields
 * filled in from two places.
 */
export function referencePatch(question: ReferenceQuestion, value: string): NotificationPatchInput | null {
  const kind = referenceKindOf(question);
  const trimmed = value.trim();
  if (!kind || !trimmed) return null;
  if (kind === 'company') return { scope: 'header', field: COMPANY_REFERENCE_FIELD, value: trimmed };
  return { scope: 'position', field: 'productNumber', value: trimmed, productId: question.productId ?? undefined };
}
